import { Button } from "@/components/ui/button";
import { ArrowRight, Flame } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { Link } from "react-router-dom";
import heroFlame from "@/assets/hero-flame.jpg";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Background layers */}
      <div className="absolute inset-0 grid-blueprint opacity-40" />
      <div className="absolute inset-0 bg-radial-fire" />
      <img
        src={heroFlame}
        alt=""
        width={1920}
        height={1280}
        className="absolute right-0 top-0 h-full w-full md:w-[70%] object-cover opacity-60 mix-blend-screen pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

      {/* Corner marks */}
      <CornerMark className="top-20 left-6" />
      <CornerMark className="top-20 right-6" rotate={90} />
      <CornerMark className="bottom-6 left-6" rotate={-90} />
      <CornerMark className="bottom-6 right-6" rotate={180} />

      <div className="container relative z-10">
        <div className="max-w-3xl space-y-8 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-full bg-card/50 backdrop-blur-sm">
            <Flame className="w-3.5 h-3.5 text-primary ember-pulse" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {t("hero.badge")}
            </span>
          </div>

          <h1 className="font-serif-display text-5xl sm:text-7xl md:text-8xl font-light leading-[0.95] tracking-tight">
            {t("hero.title1")} <br />
            <span className="text-ember italic">{t("hero.title2")}</span>
            <br />
            {t("hero.title3")}
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t("hero.description")}
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-4">
            <Link to="/">
              <Button variant="ember" size="xl">
                {t("hero.ctaChat")} <ArrowRight />
              </Button>
            </Link>
            <Link to="/taller">
              <Button variant="spark" size="xl">{t("hero.cta1")}</Button>
            </Link>
          </div>

          {/* Quick Tool Navigation Badges */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Link
              to="/herramientas/cad_bridge"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-all cursor-pointer"
            >
              📐 CAD/CAM Fusion & Bambu
            </Link>
            <Link
              to="/herramientas/solar"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-all cursor-pointer"
            >
              ☀️ Solar & LiFePO4 PR
            </Link>
            <Link
              to="/herramientas/lora"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-sky-500/10 border border-sky-500/30 text-sky-300 hover:bg-sky-500/20 transition-all cursor-pointer"
            >
              📡 LoRa Mesh US915
            </Link>
            <Link
              to="/herramientas/council"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 transition-all cursor-pointer"
            >
              🏛️ Consejo de Titanes
            </Link>
            <Link
              to="/herramientas/mcp"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-all cursor-pointer"
            >
              🔌 Web Serial USB MCP
            </Link>
            <Link
              to="/herramientas/arduino"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20 transition-all cursor-pointer"
            >
              ⚡ ESP32 & Wokwi
            </Link>
            <Link
              to="/herramientas/google_pics"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 transition-all cursor-pointer"
            >
              🏷️ Faceplates Gabinetes
            </Link>
            <Link
              to="/herramientas/inventory"
              className="px-2.5 py-1 rounded-full text-[10px] font-mono-tech bg-muted/60 border border-border text-foreground hover:bg-muted transition-all cursor-pointer"
            >
              📦 Abasto Local PR
            </Link>
          </div>

          <div className="flex items-center gap-8 pt-6 border-t border-border/50 max-w-md">
            <Stat value="13" label="Herramientas" />
            <Stat value="4x" label="Titanes LLM" />
            <Stat value="915M" label="LoRa Mesh PR" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <div className="font-serif-display text-3xl text-ember">{value}</div>
    <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</div>
  </div>
);

const CornerMark = ({ className = "", rotate = 0 }: { className?: string; rotate?: number }) => (
  <div
    className={`absolute w-6 h-6 ${className}`}
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <div className="absolute top-0 left-0 w-full h-px bg-primary/60" />
    <div className="absolute top-0 left-0 w-px h-full bg-primary/60" />
  </div>
);